import { useState } from "react";

import type { OptionValue, ValueMap } from "../types";

export type UseMultiselectFilterParams<K> = {
    allItems?: ValueMap<K>;
    appliedItems: ValueMap<K>;
    initialItems?: ValueMap<K>;
    onApplyChanges: (value: ValueMap<K>) => void;
};

export const useMultiselectFilter = <K>({
    allItems,
    appliedItems,
    initialItems = new Map(),
    onApplyChanges,
}: UseMultiselectFilterParams<K>) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempSelectedItems, setTempSelectedItems] = useState<ValueMap<K>>(initialItems);

    const onItemSelect = (item: OptionValue<K>) =>
        setTempSelectedItems((prev) => {
            const newMap = new Map(prev);
            if (newMap.has(item.id)) {
                newMap.delete(item.id);
                return newMap;
            }
            newMap.set(item.id, item);
            return newMap;
        });

    const onAllItemsClick = () => {
        if (!allItems) {
            return;
        }

        setTempSelectedItems(tempSelectedItems.size === allItems.size ? new Map() : allItems);
    };

    const onApply = () => {
        onApplyChanges(tempSelectedItems);
        setIsOpen(false);
    };

    const onReset = () => {
        setTempSelectedItems(initialItems);
        onApplyChanges(initialItems);
    };

    const onOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (open) {
            setTempSelectedItems(new Map(appliedItems));
        }
    };

    return {
        isOpen,
        tempSelectedItems,
        onItemSelect,
        onAllItemsClick,
        onApply,
        onReset,
        onOpenChange,
    };
};
